import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
    DeskTable as KitDeskTable, ElementTable as KitElementTable, ElementCell as KitElementCell,
} from '@ofself/shell';
import { ArrowUp, ArrowDown, LayoutGrid, Table2 } from 'lucide-react';
import { TitledSet } from './SetHead';
import Segmented from './Segmented';
import './DataTable.css';

// THE SHELF — every collection in Nucleus, shown two ways: as CARDS (each
// thing its own small sheet on the dot grid, its face first: personal, made
// for recognising who) or as a TABLE (one row each: made for scanning and
// comparing). The page's view is one choice, remembered per section; a page
// with any collection on it wears the Cards | Table switch in its corner.
//
// DeskTable, ElementTable and ElementCell here are drop-ins for the kit's
// (same props): in cards they are the kit's own; in the table the same items
// draw as its rows: the mark as a small face, the name, the note, the tag and
// badge.

// what each section shows first: things with a face as cards, data as rows
const DEFAULT_VIEW = {
    people: 'cards', groups: 'cards', apps: 'cards', plugins: 'cards', realms: 'cards', self: 'cards',
    graph: 'rows', activity: 'rows',
};
const KEY = (section) => `nucleus:view:${section}`;
const read = (section) => {
    try { const v = localStorage.getItem(KEY(section)); if (v === 'cards' || v === 'rows') return v; } catch { /* private mode */ }
    return DEFAULT_VIEW[section] || 'cards';
};

const ViewContext = createContext({ view: 'cards', register: () => {} });
export const useView = () => useContext(ViewContext).view;

// an address may ask for a view (?as=cards | rows; the older cells | list too)
const ASKED = { cards: 'cards', cells: 'cards', rows: 'rows', list: 'rows' };

/** The page's view, and whether it has a collection to switch: for NucleusPage. */
export function useShelf(section, asked) {
    const [chosen, setViewState] = useState(() => read(section));
    const view = ASKED[asked] || chosen;
    const [count, setCount] = useState(0);
    useEffect(() => { setViewState(read(section)); }, [section]);
    const setView = useCallback((v) => {
        setViewState(v);
        try { localStorage.setItem(KEY(section), v); } catch { /* private mode */ }
    }, [section]);
    const register = useCallback(() => { setCount((n) => n + 1); return () => setCount((n) => n - 1); }, []);
    const value = useMemo(() => ({ view, register }), [view, register]);
    // `asked` came from the address; once the switch is pressed, the choice wins
    return { view, setView, hasShelf: count > 0, value };
}
export const ShelfProvider = ViewContext.Provider;

/** The corner's switch: the segmented strip, Cards | Table, glyphs alone. */
const VIEWS = [
    { id: 'cards', label: 'Cards', icon: <LayoutGrid strokeWidth={1.8} /> },
    { id: 'rows', label: 'Table', icon: <Table2 strokeWidth={1.8} /> },
];
export function ViewSwitch({ view, onChange }) {
    return <Segmented label="Show as" options={VIEWS} value={view} onChange={onChange} size="lg" iconOnly />;
}

/** A page that draws its own two views (the Identity graph's table): it counts
 *  as a collection for the switch, and shows `rows` or `cards` by the view. */
export function ByView({ cards, rows }) {
    const { view, register } = useContext(ViewContext);
    useEffect(() => register(), [register]);
    return view === 'rows' ? rows : cards;
}

// ── the drop-ins ───────────────────────────────────────────────────────────

export function DeskTable({ children, top = 34, bottom = 72, between = 40, className = '', ...rest }) {
    const view = useView();
    if (view === 'cards') return <KitDeskTable top={top} bottom={bottom} between={between} className={className} {...rest}>{children}</KitDeskTable>;
    return <div className={`nx-shelf ${className}`} style={{ paddingTop: top, paddingBottom: bottom, gap: between }} {...rest}>{children}</div>;
}

// ── rows: the same cells as a table ────────────────────────────────────────
// In rows an ElementTable is a table (DataTable's look: a head that sorts,
// hairlines between rows, a rule between columns), and each ElementCell is one
// of its rows. A cell says what it shows (its name, note, tag, badge); the
// table keeps a column for each that some row fills, and sorts the rows by it
// — in the document, not only on screen. A page names the note and tag
// columns with `heads`; a page that wants its own columns draws a DataTable
// through ByView instead.

const ROW_COLUMNS = [
    { id: 'name', min: 200, grow: 2 },
    { id: 'note', min: 140, grow: 1.4 },
    { id: 'tag', min: 96, grow: 0 },
    { id: 'badge', min: 48, grow: 0 },
];
const HEADS = { name: 'Name', note: 'Details', tag: 'Kind', badge: '' };
const GAP = 12;
const PAD = 14;
const filled = (v) => v != null && v !== false && v !== '';
const sortable = (v) => (typeof v === 'string' || typeof v === 'number' ? v : null);
function compare(a, b) {
    if (a == null) return b == null ? 0 : 1;
    if (b == null) return -1;
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

const RowsContext = createContext(null);
const RowKey = createContext(null);

function RowsTable({ title, heads, items }) {
    const [said, setSaid] = useState({});
    const [sort, setSort] = useState(null);
    const say = useCallback((key, values) => setSaid((now) => {
        const was = now[key];
        if (was && was.name === values.name && was.note === values.note && was.tag === values.tag && was.badge === values.badge) return now;
        return { ...now, [key]: values };
    }), []);
    const forget = useCallback((key) => setSaid((now) => {
        if (!(key in now)) return now;
        const next = { ...now };
        delete next[key];
        return next;
    }), []);

    const labels = { ...HEADS, ...heads };
    const values = Object.values(said);
    const shown = ROW_COLUMNS.filter((c) => c.id === 'name' || values.some((v) => v[`has_${c.id}`]));
    const tracks = shown.map((c) => (c.grow === 0 ? `${c.min}px` : `minmax(${c.min}px, ${c.grow}fr)`)).join(' ');
    const minWidth = shown.reduce((n, c) => n + c.min, 0) + GAP * (shown.length - 1) + PAD * 2;
    const style = { gridTemplateColumns: tracks };

    const keyed = items.map((child, i) => ({ child, key: child.key ?? `#${i}`, i }));
    const ordered = sort
        ? [...keyed].sort((a, b) => {
            const x = said[a.key]?.[sort.id] ?? null;
            const y = said[b.key]?.[sort.id] ?? null;
            if ((x == null) !== (y == null)) return compare(x, y);
            return (sort.dir === 'asc' ? 1 : -1) * compare(x, y) || a.i - b.i;
        })
        : keyed;
    const sortBy = (id) => setSort((s) => ({ id, dir: s?.id === id && s.dir === 'asc' ? 'desc' : 'asc' }));
    const context = useMemo(() => ({ style, shown: shown.map((c) => c.id), say, forget }), [tracks, say, forget]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="nx-dt" role="table" aria-label={typeof title === 'string' ? title : undefined}>
            <div className="nx-dt__body" style={{ minWidth }}>
                <div className="nx-dt__head" role="row" style={style}>
                    {shown.map((c) => {
                        const on = sort?.id === c.id;
                        const label = labels[c.id];
                        return (
                            <div key={c.id} role="columnheader" className="nx-dt__th" aria-sort={on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                                {label && c.id !== 'badge'
                                    ? (
                                        <button type="button" className={`nx-dt__sort${on ? ' is-on' : ''}`} onClick={() => sortBy(c.id)}>
                                            {label}
                                            {on && (sort.dir === 'asc' ? <ArrowUp aria-hidden="true" /> : <ArrowDown aria-hidden="true" />)}
                                        </button>
                                    )
                                    : <span className="nx-dt__word">{label}</span>}
                            </div>
                        );
                    })}
                </div>
                <RowsContext.Provider value={context}>
                    {ordered.map(({ child, key }) => <RowKey.Provider key={key} value={key}>{child}</RowKey.Provider>)}
                </RowsContext.Provider>
                <div className="nx-dt__rules" style={style} aria-hidden="true">
                    {shown.map((c, i) => <span key={c.id} className={i > 0 && labels[c.id] ? 'is-ruled' : undefined} />)}
                </div>
            </div>
        </div>
    );
}

export function ElementTable({ title, count, note, empty, heads, children, className = '', columns, ...rest }) {
    const { view, register } = useContext(ViewContext);
    useEffect(() => register(), [register]);
    if (view === 'cards') return <KitElementTable title={title} count={count} note={note} empty={empty} columns={columns} className={className} {...rest}>{children}</KitElementTable>;
    const items = React.Children.toArray(children).filter(Boolean);
    return (
        <TitledSet title={title} count={count} note={note} empty={empty} className={className} {...rest}>
            {items.length > 0 && <RowsTable title={title} heads={heads} items={items} />}
        </TitledSet>
    );
}

function ElementRow({ mark, name, note, tag, badge, hue, current, dim, frame, onPress, title, className, rest }) {
    const table = useContext(RowsContext);
    const key = useContext(RowKey);
    const say = table?.say;
    const forget = table?.forget;
    const hasNote = filled(note);
    const hasTag = filled(tag);
    const hasBadge = filled(badge);
    useLayoutEffect(() => {
        if (!say || key == null) return;
        say(key, { name: sortable(name), note: sortable(note), tag: sortable(tag), badge: null, has_note: hasNote, has_tag: hasTag, has_badge: hasBadge });
    }, [say, key, name, note, tag, hasNote, hasTag, hasBadge]);
    useEffect(() => () => { if (forget && key != null) forget(key); }, [forget, key]);
    const shown = table?.shown || ['name', 'note', 'tag', 'badge'];
    const style = { ...(table?.style || { gridTemplateColumns: 'minmax(200px, 2fr) minmax(140px, 1.4fr) 96px 48px' }), ...(hue ? { '--element-hue': hue } : {}) };
    return (
        <div
            role="row" tabIndex={0} style={style} title={title} aria-selected={current || undefined}
            className={`nx-dt__row nx-erow${current ? ' is-current' : ''}${dim ? ' is-dim' : ''}${frame ? ' is-frame' : ''}${onPress ? '' : ' is-inert'} ${className}`}
            onClick={onPress} onKeyDown={(ev) => { if (onPress && (ev.key === 'Enter' || ev.key === ' ')) { ev.preventDefault(); onPress(); } }}
            {...rest}
        >
            <span role="cell" className="nx-dt__td nx-erow__lead">
                <span className="nx-erow__face" aria-hidden="true">{mark}</span>
                <span className="nx-erow__name">{name}</span>
            </span>
            {shown.includes('note') && <span role="cell" className="nx-dt__td nx-erow__note">{note}</span>}
            {shown.includes('tag') && <span role="cell" className="nx-dt__td nx-erow__tag">{tag}</span>}
            {shown.includes('badge') && <span role="cell" className="nx-dt__td nx-erow__badge">{hasBadge ? badge : null}</span>}
        </div>
    );
}

export function ElementCell({
    mark, name, note, tag, number, badge, hue, current, dim, frame, onPress, title, className = '', ...rest
}) {
    const view = useView();
    if (view === 'cards') {
        return <KitElementCell mark={mark} name={name} note={note} tag={tag} number={number} badge={badge} hue={hue} current={current} dim={dim} frame={frame} onPress={onPress} title={title} className={className} {...rest} />;
    }
    return <ElementRow mark={mark} name={name} note={note} tag={tag} badge={badge} hue={hue} current={current} dim={dim} frame={frame} onPress={onPress} title={title} className={className} rest={rest} />;
}
